import { niceAxis } from '@ardoq/math';
import type {
  AxisCounts,
  NumericRange,
  ScalarGrid,
  XyzDataPoint,
} from '../types';
import { minOf, maxOf } from './ranges';

/**
 * Pads a zero-width range so a single distinct value still produces a drawable extent rather than
 * a division by zero downstream.
 */
const padDegenerate = ([min, max]: NumericRange): NumericRange =>
  min === max ? [min - 0.5, max + 0.5] : [min, max];

const extentOf = (values: number[]): NumericRange =>
  padDegenerate([minOf(values), maxOf(values)]);

export type SampledExtent = {
  dataXRange: NumericRange;
  dataYRange: NumericRange;
};

/**
 * The domain a lattice built from these points would be sampled over.
 *
 * One implementation, called both by `toScalarGrid` and by the host that decides the axes, because
 * the two have to agree exactly. They are separated by a package boundary and by a React tree, so
 * a second copy of this arithmetic would disagree silently and in a way no type can catch: the
 * field would be drawn at a position the labels do not name, by whatever the two definitions
 * differed by.
 *
 * Over the collapsed samples rather than the raw points, because that is what the gridding
 * triangulates. Collapsing merges coincident positions and so cannot move an extent, but taking
 * the raw points here would be relying on that rather than stating it.
 */
export const toExtentOfSamples = (
  samples: { x: number; y: number }[]
): SampledExtent => {
  if (samples.length === 0) {
    return { dataXRange: [0, 1], dataYRange: [0, 1] };
  }

  return {
    dataXRange: extentOf(samples.map(({ x }) => x)),
    dataYRange: extentOf(samples.map(({ y }) => y)),
  };
};

/**
 * The same extent, for a caller that has points rather than collapsed samples.
 *
 * Reads the points directly rather than collapsing first, which is exact rather than close:
 * `collapseCoincident` groups by an exact `x|y` key and keeps the first point's coordinates, so it
 * creates no position and drops none. The set of distinct positions - and so the extent - is the
 * same either side of it. That also keeps this independent of `aggregation`, which reduces z and
 * cannot move a horizontal extent, so a host with no opinion about gridding can still derive the
 * axes.
 */
export const toSampledExtent = (points: XyzDataPoint[]): SampledExtent =>
  toExtentOfSamples(points);

export type HorizontalAxis = {
  range: NumericRange;
  /**
   * How many divisions the axis is drawn with.
   *
   * Travels with the range because the two are one answer: an axis divided five ways and ruled
   * four ways puts every label between two gridlines, which reads as a rendering fault rather
   * than as a choice about numbers.
   */
  tickCount: number;
  /**
   * How far the range runs below the first tick and above the last, in field units. `[0, 0]` for
   * an axis whose ends are ticks.
   *
   * The ticks divide what is left once this is taken off the ends, so a 1-5 axis padded by half a
   * bin still reads 1, 2, 3, 4, 5.
   */
  padding: AxisPadding;
};

/** Field units an axis runs past its first tick and past its last. */
export type AxisPadding = [low: number, high: number];

/** Fractions of an axis, 0 to 1, at its low end and its high end. */
export type AxisInset = [low: number, high: number];

/**
 * The two horizontal axes, which the host decides.
 *
 * Handed to the charts rather than derived inside them, so the four views can be made to agree
 * about the plane they share - and so the decision sits in the source-data layer with the rest of
 * the derivation rather than in a renderer.
 *
 * The height axis is not here, and cannot be: on the surface and the contour it spans the
 * *interpolated* values, which do not exist until the lattice is built, and the source-data layer
 * deliberately does no gridding. The horizontals are the shared plane; the height is per-reading.
 */
export type HorizontalAxes = {
  x: HorizontalAxis;
  y: HorizontalAxis;
};

/**
 * Divisions the 3D charts draw an axis with when nothing has chosen a count.
 *
 * Four rather than the contour's five because these are foreshortened: two of the three axes run
 * away from the eye, and labels along them crowd at the far end well before a flat axis' would.
 */
export const DEFAULT_AXIS_TICK_COUNT = 4;

/**
 * Divisions the contour draws an axis with.
 *
 * Its own number, and higher, because it is flat: nothing foreshortens, so the labels stay evenly
 * spaced and there is room for one more.
 */
export const DEFAULT_CONTOUR_AXIS_TICK_COUNT = 5;

/**
 * Axes spanning exactly the data, which is what these charts drew before the host owned them.
 *
 * The baseline a host can build on, and what the stories and tests use when the chart under test
 * is not about axes.
 */
export const toDataHorizontalAxes = (
  points: XyzDataPoint[],
  /* Stated by the caller rather than defaulted, because the two chart families genuinely want
     different counts and a default here would silently give a contour the 3D charts' four. */
  tickCount: number
): HorizontalAxes => {
  const { dataXRange, dataYRange } = toSampledExtent(points);

  return {
    x: { range: dataXRange, tickCount, padding: [0, 0] },
    y: { range: dataYRange, tickCount, padding: [0, 0] },
  };
};

/**
 * Axes rounded to the numbers a reader would name, each with its own tick count.
 *
 * Widened outward, and the lattice is still sampled over the data's own extent - so the plot
 * occupies a sub-rectangle of the axes. {@link toLatticeFrame} is what puts it there.
 */
export const toNiceHorizontalAxes = (
  points: XyzDataPoint[]
): HorizontalAxes => {
  const { dataXRange, dataYRange } = toSampledExtent(points);
  const x = niceAxis(dataXRange);
  const y = niceAxis(dataYRange);

  return {
    x: { range: x.range, tickCount: x.tickCount, padding: [0, 0] },
    y: { range: y.range, tickCount: y.tickCount, padding: [0, 0] },
  };
};

const padToEdgeBins = (
  axis: HorizontalAxis,
  [dataMin, dataMax]: NumericRange,
  binCount: number
): HorizontalAxis => {
  if (binCount < 2) return axis;
  const halfBin = (dataMax - dataMin) / (binCount - 1) / 2;
  const firstTick = axis.range[0] + axis.padding[0];
  const lastTick = axis.range[1] - axis.padding[1];
  const low = Math.max(0, halfBin - (dataMin - firstTick));
  const high = Math.max(0, halfBin - (lastTick - dataMax));

  return {
    range: [firstTick - low, lastTick + high],
    tickCount: axis.tickCount,
    padding: [low, high],
  };
};

/**
 * The axes widened just enough at each end that the edge bins fit, for a `binned` chart of these
 * points.
 *
 * Bins are centered on lattice points, so the first and last stand on the data's extent and reach
 * half a bin past it. An end that already has that much room gets nothing, and with no points
 * there are no columns to fit. The ticks stay where they were.
 */
export const toBinnedHorizontalAxes = (
  axes: HorizontalAxes,
  points: XyzDataPoint[],
  binCounts: AxisCounts
): HorizontalAxes => {
  if (points.length === 0) return axes;
  const { dataXRange, dataYRange } = toSampledExtent(points);

  return {
    x: padToEdgeBins(axes.x, dataXRange, binCounts.x),
    y: padToEdgeBins(axes.y, dataYRange, binCounts.y),
  };
};

/** A tick: the value it reads, and where it falls as a fraction of the axis from its low end. */
export type AxisTick = { value: number; fraction: number };

/** The fraction of an axis its padding takes at each end. */
export const toTickInset = ({
  range: [min, max],
  padding: [low, high],
}: HorizontalAxis): AxisInset => {
  const span = max - min;
  return span > 0 ? [low / span, high / span] : [0, 0];
};

/** Field units between neighboring ticks; 0 for an axis with no divisions. */
export const toTickStep = ({
  range: [min, max],
  padding: [low, high],
  tickCount,
}: HorizontalAxis): number =>
  tickCount > 0 ? (max - min - low - high) / tickCount : 0;

/** Where the `tickCount + 1` ticks fall, as fractions of the axis from its low end. */
export const toTickFractions = (
  tickCount: number,
  [lowInset, highInset]: AxisInset
): number[] =>
  Array.from(
    { length: tickCount + 1 },
    (_, index) => lowInset + (index / tickCount) * (1 - lowInset - highInset)
  );

/** The `tickCount + 1` ticks of an axis, evenly dividing its range less the padding. */
export const toAxisTicks = (axis: HorizontalAxis): AxisTick[] => {
  const {
    range: [min, max],
    tickCount,
    padding,
  } = axis;
  const low = min + padding[0];
  const high = max - padding[1];

  return toTickFractions(tickCount, toTickInset(axis)).map(
    (fraction, index) => ({
      value: low + (index / tickCount) * (high - low),
      fraction,
    })
  );
};

/**
 * Where `[min, max]` sits inside `axis`, as a fraction of it.
 *
 * A degenerate axis answers the whole span.
 */
export const toAxisFractions = (
  [min, max]: NumericRange,
  [axisMin, axisMax]: NumericRange
): [low: number, high: number] => {
  const span = axisMax - axisMin;
  if (!(span > 0)) return [0, 1];

  return [(min - axisMin) / span, (max - axisMin) / span];
};

export type LatticeFrame = {
  /** A lattice column in `[0, width - 1]` to a cube x in `[-1, 1]`. */
  toCubeX: (gridX: number) => number;
  /** A lattice row in `[0, height - 1]` to a cube y in `[-1, 1]`. */
  toCubeY: (gridY: number) => number;
};

/**
 * Where the lattice sits in the camera's cube, once the axes around it have been widened.
 *
 * Derived once: the mesh and the isolines both come back in lattice coordinates and have to land
 * in the same place.
 */
export const toLatticeFrame = (grid: ScalarGrid): LatticeFrame => {
  const [xLow, xHigh] = toAxisFractions(grid.dataXRange, grid.xRange);
  const [yLow, yHigh] = toAxisFractions(grid.dataYRange, grid.yRange);
  const columns = grid.width - 1 || 1;
  const rows = grid.height - 1 || 1;

  return {
    toCubeX: gridX => (xLow + (gridX / columns) * (xHigh - xLow)) * 2 - 1,
    toCubeY: gridY => (yLow + (gridY / rows) * (yHigh - yLow)) * 2 - 1,
  };
};
